// Copyright (c) Microsoft Corporation. All rights reserved.
// Licensed under the MIT License.
import { _warnToConsole, dateNow, dumpObj, getJSON, hasJSON, throwError, utlCanUseSessionStorage, utlGetSessionStorage, utlRemoveSessionStorage, utlSetSessionStorage } from "@microsoft/applicationinsights-core-js";
import { _DYN_PAGE_VISIT_TIME, _DYN_TRACK_PREVIOUS_PAGE_1 } from "../../__DynamicConstants";
/**
 * Factory function to create a page visit data object
 * @param pageName - Name of the page
 * @param pageUrl - URL of the page
 * @returns IPageVisitData instance
 */
export function createPageVisitData(pageName, pageUrl) {
    var _a;
    return _a = {
            pageVisitStartTime: dateNow(),
            pageName: pageName,
            pageUrl: pageUrl
        },
        _a[_DYN_PAGE_VISIT_TIME /* @min:pageVisitTime */] = 0,
        _a;
}
/**
 * Factory function to create a PageVisitTimeManager instance.
 * @param logger - Diagnostic logger
 * @param pageVisitTimeTrackingHandler - Delegate that will be called to send telemetry data to AI (when trackPreviousPageVisit is called)
 * @returns A new IPageVisitTimeManager instance.
 * @internal
 */
export function createPageVisitTimeManager(logger, pageVisitTimeTrackingHandler) {
    var _a;
    var prevPageVisitDataKeyName = "prevPageVisitData";
    /**
     * Stops timing of current page (if exists) and starts timing for duration of visit to pageName
     * @param pageName - Name of page to begin timing visit duration
     * @returns {IPageVisitData} Page visit data (including duration) of pageName from last call to start or restart, if exists. Null if not.
     */
    function restartPageVisitTimer(pageName, pageUrl) {
        var prevPageVisitData = null;
        try {
            prevPageVisitData = stopPageVisitTimer();
            if (utlCanUseSessionStorage()) {
                if (utlGetSessionStorage(logger, prevPageVisitDataKeyName) != null) {
                    throwError("Cannot call startPageVisit consecutively without first calling stopPageVisit");
                }
                var currPageVisitDataStr = getJSON().stringify(createPageVisitData(pageName, pageUrl));
                utlSetSessionStorage(logger, prevPageVisitDataKeyName, currPageVisitDataStr);
            }
        }
        catch (e) {
            _warnToConsole(logger, "Call to restart failed: " + dumpObj(e));
            prevPageVisitData = null;
        }
        return prevPageVisitData;
    }
    /**
     * Stops timing of current page, if exists.
     * @returns {IPageVisitData} Page visit data (including duration) of pageName from call to start, if exists. Null if not.
     */
    function stopPageVisitTimer() {
        var prevPageVisitData = null;
        try {
            if (utlCanUseSessionStorage()) {
                // Define end time of page's visit
                var pageVisitEndTime = dateNow();
                // Try to retrieve  page name and start time from session storage
                var pageVisitDataJsonStr = utlGetSessionStorage(logger, prevPageVisitDataKeyName);
                if (pageVisitDataJsonStr && hasJSON()) {
                    // if previous page data exists, set end time of visit
                    prevPageVisitData = getJSON().parse(pageVisitDataJsonStr);
                    prevPageVisitData[_DYN_PAGE_VISIT_TIME /* @min:%2epageVisitTime */] = pageVisitEndTime - prevPageVisitData.pageVisitStartTime;
                    // Remove data from storage since we already used it
                    utlRemoveSessionStorage(logger, prevPageVisitDataKeyName);
                }
            }
        }
        catch (e) {
            _warnToConsole(logger, "Stop page visit timer failed: " + dumpObj(e));
            prevPageVisitData = null;
        }
        return prevPageVisitData;
    }
    return _a = {},
        _a[_DYN_TRACK_PREVIOUS_PAGE_1 /* @min:trackPreviousPageVisit */] = function (currentPageName, currentPageUrl) {
            try {
                // Restart timer for new page view
                var prevPageVisitTimeData = restartPageVisitTimer(currentPageName, currentPageUrl);
                // If there was a page already being timed, track the visit time for it now.
                if (prevPageVisitTimeData) {
                    pageVisitTimeTrackingHandler(prevPageVisitTimeData.pageName, prevPageVisitTimeData.pageUrl, prevPageVisitTimeData[_DYN_PAGE_VISIT_TIME /* @min:%2epageVisitTime */]);
                }
            }
            catch (e) {
                _warnToConsole(logger, "Auto track page visit time failed, metric will not be collected: " + dumpObj(e));
            }
        },
        // Expose for backward compatibility with tests
        _a._logger = logger,
        _a.pageVisitTimeTrackingHandler = pageVisitTimeTrackingHandler,
        _a;
}
//# sourceMappingURL=PageVisitTimeManager.js.map